import { compareDesc, differenceInDays } from 'date-fns';

import { GoalThreshold } from '../../coaching-base/config';
import {
    CoachingEventStatus,
    CommitmentSummaryListItem,
    DraftAndApprovalResult,
    GoalStatus,
} from '../../coaching-base/general';

import { CommitmentSummaryStatusView } from './commitment-summary-status-view';
import { CommitmentSummaryView } from './commitment-summary-view.model';
import { TranslationKeyMap } from './pillar-two.i18n';

const commitmentStatusSummaryStatusViewMap: {
    [key in CoachingEventStatus]: CommitmentSummaryStatusView;
} = {
    [CoachingEventStatus.DRAFT]: CommitmentSummaryStatusView.DRAFT,
    [CoachingEventStatus.PRE_APPROVAL]:
        CommitmentSummaryStatusView.PRE_APPROVAL,
    [CoachingEventStatus.ACTIVE]: CommitmentSummaryStatusView.ACTIVE,
    [CoachingEventStatus.UPDATING]: CommitmentSummaryStatusView.UPDATING,
    [CoachingEventStatus.PENDING_CLOSE]:
        CommitmentSummaryStatusView.PENDING_CLOSE,
    [CoachingEventStatus.CLOSED]: CommitmentSummaryStatusView.CLOSED,
    [CoachingEventStatus.DISPUTED]: CommitmentSummaryStatusView.DRAFT, // match draft status. this will be overridden elsewhere as conditionally needed
    [CoachingEventStatus.DELETED]: null,
};

const commitmentSummaryStatusViewAddEdit = [
    CommitmentSummaryStatusView.ACTIVE,
    CommitmentSummaryStatusView.OTHER_ACTIVE,
    CommitmentSummaryStatusView.DRAFT,
];

const coachingEventStatusTranslationMap: {
    [key in CoachingEventStatus]: string;
} = {
    [CoachingEventStatus.DRAFT]: TranslationKeyMap._9476_DRAFT,
    [CoachingEventStatus.PRE_APPROVAL]:
        TranslationKeyMap._9791_PENDING_APPROVAL,
    [CoachingEventStatus.ACTIVE]: TranslationKeyMap._125_ACTIVE,
    [CoachingEventStatus.UPDATING]: TranslationKeyMap._9791_PENDING_APPROVAL,
    [CoachingEventStatus.PENDING_CLOSE]: TranslationKeyMap._7441_PENDING_CLOSE,
    [CoachingEventStatus.CLOSED]: TranslationKeyMap._7440_CLOSED,
    [CoachingEventStatus.DISPUTED]: TranslationKeyMap._9476_DRAFT,
    [CoachingEventStatus.DELETED]: null,
};

const coachingEventStatusBgColorMap: { [key in CoachingEventStatus]: string } =
    {
        [CoachingEventStatus.DRAFT]: '#505050',
        [CoachingEventStatus.PRE_APPROVAL]: '#7260bf',
        [CoachingEventStatus.ACTIVE]: '#137e0f',
        [CoachingEventStatus.UPDATING]: '#7260bf',
        [CoachingEventStatus.PENDING_CLOSE]: '#7260bf',
        [CoachingEventStatus.CLOSED]: '#bababa',
        [CoachingEventStatus.DISPUTED]: '#505050', // match draft colour. this will be overridden elsewhere as conditionally needed
        [CoachingEventStatus.DELETED]: null,
    };

const coachingEventStatusTextColorMap: {
    [key in CoachingEventStatus]: string;
} = {
    [CoachingEventStatus.DRAFT]: '#fff',
    [CoachingEventStatus.PRE_APPROVAL]: '#fff',
    [CoachingEventStatus.ACTIVE]: '#fff',
    [CoachingEventStatus.UPDATING]: '#fff',
    [CoachingEventStatus.PENDING_CLOSE]: '#fff',
    [CoachingEventStatus.CLOSED]: '#000',
    [CoachingEventStatus.DISPUTED]: '#fff',
    [CoachingEventStatus.DELETED]: null,
};

const coachingEventStatusIconMap: { [key in CoachingEventStatus]: string } = {
    [CoachingEventStatus.DRAFT]: 'draft-icon',
    [CoachingEventStatus.PRE_APPROVAL]: 'pending-icon',
    [CoachingEventStatus.ACTIVE]: 'thumb-up-outline',
    [CoachingEventStatus.UPDATING]: 'pending-icon',
    [CoachingEventStatus.PENDING_CLOSE]: 'pending-icon',
    [CoachingEventStatus.CLOSED]: 'close-box-outline',
    [CoachingEventStatus.DISPUTED]: 'draft-icon', // match draft icon. this will be overridden elsewhere as conditionally needed
    [CoachingEventStatus.DELETED]: null,
};

function isDisputedByMe(
    daa: DraftAndApprovalResult,
    authUserId: number,
): boolean {
    if (!daa) {
        return false;
    }
    const senderSide =
        (daa.approved === false && daa.targetId === authUserId) ||
        (daa.previousApproved === false && daa.previousTargetId === authUserId);
    const receiverSide =
        daa.targetedApproved === false && daa.targetedTargetId === authUserId;
    return senderSide || receiverSide;
}

function isDisputedByOtherParticipant(
    daa: DraftAndApprovalResult,
    authUserId: number,
): boolean {
    if (!daa) {
        return false;
    }
    const senderSide =
        (daa.approved === false && daa.targetId !== authUserId) ||
        (daa.previousApproved === false && daa.previousTargetId !== authUserId);
    const receiverSide =
        daa.targetedApproved === false && daa.targetedTargetId !== authUserId;
    return senderSide || receiverSide;
}

/** @internal only exported for testing */
export function determineStatusLabel(
    statusId: CoachingEventStatus,
    disputedByMe: boolean,
    disputedByOtherParticipant: boolean,
    isOriginalCommitment: boolean,
    hasDraft: boolean,
): string {
    let result = coachingEventStatusTranslationMap[statusId];
    const disputed = disputedByMe || disputedByOtherParticipant;

    // maybe the existence of DraftAndApprovalResult.approvalId!=null/DraftAndApprovalResult.approved==null should be checked instead
    const isPendingApprovalAfterLastDispute =
        statusId === CoachingEventStatus.PRE_APPROVAL ||
        statusId === CoachingEventStatus.UPDATING ||
        statusId === CoachingEventStatus.PENDING_CLOSE;

    if (
        isOriginalCommitment &&
        disputed &&
        !isPendingApprovalAfterLastDispute
    ) {
        result = TranslationKeyMap._9792_BEING_DISPUTED;
    } else if (
        !isOriginalCommitment &&
        disputedByMe &&
        !isPendingApprovalAfterLastDispute
    ) {
        result = TranslationKeyMap._9793_DISPUTED_BY_ME;
    } else if (
        !isOriginalCommitment &&
        disputedByOtherParticipant &&
        !isPendingApprovalAfterLastDispute
    ) {
        result = TranslationKeyMap._8703_DISPUTED;
    } else if (
        isOriginalCommitment &&
        hasDraft &&
        statusId === CoachingEventStatus.ACTIVE
    ) {
        result = TranslationKeyMap._9814_ACTIVE__WITH_A_DRAFT_VERSION_;
    } else if (
        !isOriginalCommitment &&
        statusId === CoachingEventStatus.ACTIVE
    ) {
        result = TranslationKeyMap._9816_DRAFT__WITH_AN_ACTIVE_VERSION_;
    }
    return result;
}

/** @internal only exported for testing */
export function determineColors(
    statusId: CoachingEventStatus,
    disputedByMe: boolean,
    disputedByOtherParticipant: boolean,
    isOriginalCommitment: boolean,
): { statusBgColor: string; statusTextColor: string; statusIcon: string } {
    let statusBgColor = coachingEventStatusBgColorMap[statusId];
    const statusTextColor = coachingEventStatusTextColorMap[statusId];
    let statusIcon = coachingEventStatusIconMap[statusId];
    const disputed = disputedByMe || disputedByOtherParticipant;

    // maybe the existence of DraftAndApprovalResult.approvalId!=null/DraftAndApprovalResult.approved==null should be checked instead
    const isPendingApprovalAfterLastDispute =
        statusId === CoachingEventStatus.PRE_APPROVAL ||
        statusId === CoachingEventStatus.UPDATING ||
        statusId === CoachingEventStatus.PENDING_CLOSE;

    if (
        isOriginalCommitment &&
        disputed &&
        !isPendingApprovalAfterLastDispute
    ) {
        statusBgColor = '#ce2521';
        statusIcon = 'thumb-down-outline';
    } else if (
        !isOriginalCommitment &&
        disputed &&
        !isPendingApprovalAfterLastDispute
    ) {
        statusBgColor = '#ce2521';
        statusIcon = 'thumb-down-outline';
    } else if (
        !isOriginalCommitment &&
        statusId === CoachingEventStatus.ACTIVE
    ) {
        statusBgColor = '#505050';
        statusIcon = 'draft-icon';
    }
    return { statusBgColor, statusTextColor, statusIcon };
}

// TODO revise. consider moving to a separate file
export function determineActions(
    statusId: CommitmentSummaryStatusView,
    disputedByMe: boolean,
    disputedByOtherParticipant: boolean,
    isOriginalCommitment: boolean,
): { hasEditAction: boolean; hasAddAction: boolean } {
    let hasEditAction = commitmentSummaryStatusViewAddEdit.includes(statusId);
    let hasAddAction = commitmentSummaryStatusViewAddEdit.includes(statusId);
    const disputed = disputedByMe || disputedByOtherParticipant;
    if (
        isOriginalCommitment &&
        disputed /* || hasDraft TODO revise. maybe this can be enabled at some point */
    ) {
        hasEditAction = false;
        hasAddAction = false;
    }
    return { hasEditAction, hasAddAction };
}

// TODO sync and consolidate with goal-category-item-view.transformer->determineDaysToAchievement()
// TODO move to separate file
export function determineDaysToAchievement(
    estimatedCompletionDate: Date,
    startDate: Date,
    endDate: Date,
    statusId: GoalStatus,
    goalThresholds: GoalThreshold[],
): {
    daysToAchievementIcon: string;
    daysToAchievementColor: string;
    daysToAchievementLabel: string;
    daysToAchievement: number;
    daysActive: number;
} {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    let daysToAchievement = Math.abs(
        differenceInDays(now, estimatedCompletionDate),
    );
    if (estimatedCompletionDate < now) {
        daysToAchievement *= -1;
    }

    let daysToAchievementLabel: string;
    if (statusId === GoalStatus.CLOSED) {
        daysToAchievementLabel = TranslationKeyMap._7440_CLOSED;
        daysToAchievement = null;
    } else {
        if (daysToAchievement < 0) {
            daysToAchievementLabel = TranslationKeyMap._9809_MISSED;
        }
        if (daysToAchievement === 0) {
            daysToAchievementLabel = TranslationKeyMap._9569_TODAY;
        }
        if (daysToAchievement == null || isNaN(daysToAchievement)) {
            daysToAchievementLabel = '-';
        }
    }

    const dateToCheck =
        statusId === GoalStatus.CLOSED ? endDate : estimatedCompletionDate;
    let daysActive = differenceInDays(dateToCheck, startDate);
    if (isNaN(daysActive)) {
        daysActive = null;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    let goalThreshold = {} as GoalThreshold;
    if (daysToAchievement != null) {
        goalThreshold =
            goalThresholds.find((e) => daysToAchievement < e.threshold) ||
            goalThreshold;
    }

    return {
        daysToAchievementIcon: goalThreshold.icon,
        daysToAchievementColor: goalThreshold.color,
        daysToAchievementLabel,
        daysToAchievement,
        daysActive,
    };
}

export function composeCommitmentSummaryItemIdentifier<
    T extends { commitmentId: number; draftId: string },
>(obj: T): string {
    return `${
        obj.commitmentId != null && obj.commitmentId !== 0
            ? obj.commitmentId
            : undefined
    }--${(obj.draftId || '').toLowerCase() || undefined}`;
}

export function parseCommitmentSummaryItemIdentifier(obj: string): {
    commitmentId: number;
    draftId: string;
} {
    let commitmentId: number;
    let draftId: string;
    if (obj) {
        const parts = obj.split('--');
        if (parts.length === 2) {
            commitmentId = parseInt(parts[0]);
            commitmentId = isNaN(commitmentId) ? undefined : commitmentId;
            draftId = parts[1].toLowerCase();
            draftId =
                draftId === 'null' || draftId === 'undefined'
                    ? undefined
                    : draftId;
        }
    }
    return { commitmentId, draftId };
}

export function commitmentSummaryToViewTransformer(
    items: CommitmentSummaryListItem[],
    userId: number,
    activeMetricId: number,
    goalThresholds: GoalThreshold[],
): CommitmentSummaryView[] {
    const result: CommitmentSummaryView[] = [];

    const gThresholds = (goalThresholds ? [...goalThresholds] : []).sort(
        (a, b) => a.threshold - b.threshold,
    );

    items &&
        [...items]
            .sort((a, b) => {
                return compareDesc(
                    new Date(a.updatedOn || a.createdOn),
                    new Date(b.updatedOn || b.createdOn),
                );
            })
            .forEach((item) => {
                const statusId =
                    commitmentStatusSummaryStatusViewMap[item.statusId];
                if (statusId == null) {
                    return;
                }

                /* for testability reasons determineStatusLabel, determineColors, determineActions
            do not take the consolidated beingDisputed argument */

                const daa = item.draftAndApproval || {};
                const disputedByMe = isDisputedByMe(daa, userId);
                const disputedByOtherParticipant = isDisputedByOtherParticipant(
                    daa,
                    userId,
                );
                const statusLabel = determineStatusLabel(
                    item.statusId,
                    disputedByMe,
                    disputedByOtherParticipant,
                    daa.isOriginalCommitment,
                    !!daa.draftId,
                );
                const { statusBgColor, statusTextColor, statusIcon } =
                    determineColors(
                        item.statusId,
                        disputedByMe,
                        disputedByOtherParticipant,
                        daa.isOriginalCommitment,
                    );
                const { hasEditAction, hasAddAction } = determineActions(
                    statusId,
                    disputedByMe,
                    disputedByOtherParticipant,
                    daa.isOriginalCommitment,
                );

                const goals = !item.goals
                    ? []
                    : [...item.goals]
                          .sort((a, b) => {
                              return compareDesc(
                                  new Date(a.updatedOn || a.createdOn),
                                  new Date(b.updatedOn || b.createdOn),
                              );
                          })
                          .map((goal) => {
                              const daysToAchievement =
                                  determineDaysToAchievement(
                                      goal.estimatedCompletionDate,
                                      goal.startDate,
                                      goal.endDate,
                                      goal.statusId,
                                      gThresholds,
                                  );

                              return {
                                  goalId: goal.goalId,
                                  metricId: goal.metricId,
                                  goalValue: goal.goalValue,
                                  metricShortName: goal.metricShortName,
                                  initialActual: goal.initialActual,
                                  initialPctToGoal: goal.initialPctGoal,
                                  initialPctToTarget: goal.initialPctTarget,
                                  currentActual: goal.currentActual,
                                  currentPctToGoal:
                                      goal.statusId === GoalStatus.DRAFT
                                          ? null
                                          : goal.currentPctGoal,
                                  currentPctToTarget: goal.currentPctTarget,
                                  createdOn: goal.createdOn,
                                  estimatedCompletionDate:
                                      goal.estimatedCompletionDate,
                                  status: coachingEventStatusTranslationMap[
                                      goal.statusId
                                  ],
                                  statusId: goal.statusId,
                                  ...daysToAchievement,
                              };
                          });

                const next: CommitmentSummaryView = {
                    commitmentId: item.commitmentId,
                    statusId,
                    statusLabel,
                    statusBgColor,
                    statusTextColor,
                    statusIcon,
                    behaviour: item.behaviourName,
                    subBehaviour: item.subBehaviourName,
                    hasEditAction,
                    hasAddAction,
                    disputedByMe,
                    disputedByOtherParticipant,
                    beingDisputed:
                        daa.isOriginalCommitment &&
                        (disputedByMe || disputedByOtherParticipant),
                    opportunityToDemonstrate: item.opportunityToDemonstrate,
                    willingnessToTry: item.willingnessToTry,
                    proficiencyDelivered: item.proficiencyDelivered,
                    goals,
                    behaviourId: item.behaviourId,
                    subBehaviourId: item.subBehaviourId,
                    isOriginalCommitment: daa.isOriginalCommitment,
                    draftId: daa.draftId,
                    approvalId: daa.approvalId,
                    approved: daa.approved,
                    targetedDraftId: daa.targetedDraftId,
                    targetedApprovalId: daa.targetedApprovalId,
                    cmtIdentifier: composeCommitmentSummaryItemIdentifier({
                        commitmentId: item.commitmentId,
                        draftId: daa.isOriginalCommitment
                            ? null
                            : daa.draftId || daa.targetedDraftId,
                    }),
                };

                result.push(next);
            });

    return result;
}
